import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, query, onSnapshot, doc, getDoc, updateDoc, serverTimestamp, orderBy } from 'firebase/firestore';

function timeAgo(date) {
  if (!date) return '';
  const secs = Math.floor((Date.now() - date.getTime()) / 1000);
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function daysOverdue(date) {
  if (!date) return 0;
  return Math.floor((Date.now() - date.getTime()) / 86400000);
}

export default function AdminReminders() {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [confirming, setConfirming] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'invoices'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, snap => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      // Only show unpaid invoices
      setInvoices(all.filter(inv => !inv.paid));
      setLoading(false);
    });
  }, []);

  // Invoices awaiting reminder confirmation (bank transfer viewed 40+ hours ago, not paid, reminder not yet sent)
  const pendingConfirmation = invoices.filter(inv => {
    if (!inv.bankTransferViewed || inv.paid || inv.reminderSuppressed) return false;
    const viewedAt = inv.bankTransferViewedAt?.toDate?.();
    if (!viewedAt) return false;
    const hoursAgo = (Date.now() - viewedAt.getTime()) / 3600000;
    return hoursAgo >= 40 && !inv.reminder1SentAt; // 40hrs = 8hr buffer before 48hr reminder fires
  });

  // All overdue invoices
  const overdue = invoices.filter(inv => {
    const created = inv.createdAt?.toDate?.();
    if (!created) return false;
    return daysOverdue(created) >= 7;
  });

  async function markPaid(invoiceId) {
    try {
      await updateDoc(doc(db, 'invoices', invoiceId), {
        paid: true,
        paidAt: serverTimestamp(),
        paidManually: true,
      });
      const inv = invoices.find(i => i.id === invoiceId);
      if (inv?.quoteId && !inv.recurring) {
        const quoteRef = doc(db, 'quotes', inv.quoteId);
        const quoteSnap = await getDoc(quoteRef);
        if (quoteSnap.exists()) {
          await updateDoc(quoteRef, { status: 'paid' });
        } else {
          console.warn(`Quote ${inv.quoteId} no longer exists — skipping status update.`);
        }
      }
    } catch (err) {
      alert('Could not mark invoice as paid. Please try again.');
    }
    setConfirming(null);
  }

  async function suppressReminder(invoiceId) {
    await updateDoc(doc(db, 'invoices', invoiceId), {
      reminderSuppressed: true,
      reminderSuppressedAt: serverTimestamp(),
    });
    setConfirming(null);
  }

  async function allowReminder(invoiceId) {
    // Clear suppression — reminder will fire at next Cloud Function run
    await updateDoc(doc(db, 'invoices', invoiceId), {
      reminderConfirmedAt: serverTimestamp(),
      reminderSuppressed: false,
    });
    setConfirming(null);
  }

  const StatusCard = ({ inv, showConfirmWindow }) => {
    const created = inv.createdAt?.toDate?.();
    const days = daysOverdue(created);
    const viewedAt = inv.bankTransferViewedAt?.toDate?.();
    const isConfirming = confirming === inv.id;

    return (
      <div className="card" style={{ marginBottom: 12,
        borderLeft: `4px solid ${days >= 30 ? '#C0392B' : days >= 14 ? 'var(--gold)' : 'var(--border)'}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <div style={{ fontWeight: 700 }}>{inv.clientName || inv.clientEmail}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{inv.clientEmail}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem' }}>£{inv.amount?.toFixed(2)}</div>
            <div style={{ fontSize: '0.75rem', color: days >= 14 ? '#C0392B' : 'var(--text-muted)', fontWeight: 600 }}>
              {days === 0 ? 'Due today' : `${days} day${days > 1 ? 's' : ''} overdue`}
            </div>
          </div>
        </div>

        <div style={{ fontSize: '0.95rem', color: 'var(--text-secondary)', marginBottom: 8 }}>
          {inv.description}
        </div>

        {inv.reminder1SentAt && (
          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 8 }}>
            Reminder 1 sent · {inv.reminder2SentAt ? 'Reminder 2 sent' : 'Reminder 2 pending'}
            {inv.reminder3SentAt && ' · Final notice sent'}
          </div>
        )}

        {/* 48-hour confirmation window */}
        {showConfirmWindow && !isConfirming && (
          <div style={{ background: '#FFF3CD', border: '1px solid #FFC107', borderRadius: 10,
            padding: '12px 14px', marginTop: 8 }}>
            <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#856404', marginBottom: 6 }}>
              ⏰ Reminder due to send in ~8 hours
            </div>
            <div style={{ fontSize: '0.95rem', color: '#856404', marginBottom: 10 }}>
              Client viewed bank details {viewedAt ? timeAgo(viewedAt) : ''}. Has payment arrived?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setConfirming(inv.id)}
                style={{ flex: 1, background: 'var(--green-mid)', color: 'white', border: 'none',
                  borderRadius: 8, padding: '9px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                ✓ Mark as paid
              </button>
              <button onClick={() => allowReminder(inv.id)}
                style={{ flex: 1, background: '#FFC107', color: '#333', border: 'none',
                  borderRadius: 8, padding: '9px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
                Send reminder
              </button>
            </div>
          </div>
        )}

        {/* Confirming paid */}
        {isConfirming && (
          <div style={{ background: 'var(--green-pale)', borderRadius: 10, padding: '14px',
            border: '1px solid var(--green-light)', marginTop: 8 }}>
            <div style={{ fontWeight: 600, marginBottom: 10, color: 'var(--green-dark)' }}>
              Confirm payment received from {inv.clientName || inv.clientEmail}?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => markPaid(inv.id)}
                style={{ flex: 2, background: 'var(--green-mid)', color: 'white', border: 'none',
                  borderRadius: 8, padding: '10px', fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer' }}>
                ✓ Yes, mark paid
              </button>
              <button onClick={() => setConfirming(null)}
                style={{ flex: 1, background: 'white', color: 'var(--text-secondary)',
                  border: '1px solid var(--border)', borderRadius: 8, padding: '10px',
                  fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Standard mark paid for non-confirmation items */}
        {!showConfirmWindow && !isConfirming && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => setConfirming(inv.id)}
              style={{ flex: 1, background: 'var(--green-pale)', color: 'var(--green-dark)',
                border: '1px solid var(--green-light)', borderRadius: 8, padding: '8px',
                fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
              Mark as paid
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="app-header">
        <button onClick={() => navigate('/admin/dashboard')}
          style={{ background: 'none', color: 'white', fontSize: '1.2rem', width: 'auto', padding: '0 12px 0 0' }}>←</button>
        <div>
          <h1>Reminders & payments</h1>
          <div className="subtitle">Outstanding invoices</div>
        </div>
      </div>

      <div className="page-wrap" style={{ paddingTop: 24 }}>

        {loading && <div className="spinner" />}

        {/* Confirmation window alerts */}
        {pendingConfirmation.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <h2 className="section-title" style={{ margin: 0 }}>Action needed</h2>
              <span style={{ background: '#FFC107', color: '#333', borderRadius: '50%', width: 22,
                height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700 }}>{pendingConfirmation.length}</span>
            </div>
            <p style={{ fontSize: '1rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
              These clients chose bank transfer and a reminder is about to send automatically.
              Please confirm whether payment has arrived.
            </p>
            {pendingConfirmation.map(inv => (
              <StatusCard key={inv.id} inv={inv} showConfirmWindow={true} />
            ))}
            <hr className="divider" />
          </>
        )}

        {/* All overdue */}
        <h2 className="section-title">All outstanding invoices</h2>
        {!loading && overdue.length === 0 && invoices.length === 0 && (
          <div className="empty-state">
            <p>🎉 No outstanding invoices!</p>
          </div>
        )}
        {invoices
          .filter(inv => !pendingConfirmation.find(p => p.id === inv.id))
          .map(inv => (
            <StatusCard key={inv.id} inv={inv} showConfirmWindow={false} />
          ))}
      </div>
    </>
  );
}
